import { T, useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useState } from 'react';

import Crisis from '@/assets/images/onboarding/crisis.svg';
import { useRulesEngine } from '@/modules/rulesEngine';
import { CtaButton, Screen, ScreenHeading } from '@/modules/ui';

/**
 * CRISIS PROGRAM SELECTION — reached when the PHQ/GAD result requires the crisis
 * line (Sparta's CrisisProgramContainer / CrisisProgramPage). Presents the
 * supportive "a video call with a Vida therapist is the next step" message;
 * Continue enrolls the qualified program (seeded by `enterCrisisProgram`) and
 * the driver advances to coach matching.
 *
 * Mirrors Sparta's flag-OFF path (show the page, then enroll on Continue) rather
 * than the SPARTA_AUTO_JOIN_PROGRAM auto-join — surfacing the crisis messaging
 * is the safer UX, and onboarding has no auto-join path yet.
 */
export function CrisisProgramSelectionScreen() {
  const t = useT();
  const { enrollInProgram } = useRulesEngine();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const onContinue = async () => {
    setSubmitError(false);
    try {
      setIsSubmitting(true);
      // Enrolling sets a program instance, so the driver re-resolves past the
      // crisis gate into coach matching.
      await enrollInProgram();
    } catch {
      setSubmitError(true);
      setIsSubmitting(false);
    }
  };

  return (
    <Screen
      busy={isSubmitting}
      contentStyle={{ justifyContent: 'center' }}
      footer={
        <CtaButton
          isBusy={isSubmitting}
          isDisabled={isSubmitting}
          onPress={onContinue}
          testID='crisis-program-continue'
        >
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='4xl' className='items-center'>
        <Crisis width={200} height={174} />
        <VStack space='xl' className='items-center'>
          <ScreenHeading title={t('We’d like to learn more about your needs')} center />
          <Text size='md' className='text-center text-typography-900'>
            <T _str='Based on your survey answers, a video call with a Vida therapist is the next step. They’ll connect you with the right Vida services, a referral to a therapist in your area, or both.' />
          </Text>
          <Text size='md' className='text-center font-body-semibold text-typography-900'>
            <T _str='We’re here for you.' />
          </Text>
        </VStack>
        {submitError ? (
          <Text size='sm' className='text-center text-error-700'>
            <T _str='Something went wrong. Please try again.' />
          </Text>
        ) : null}
      </VStack>
    </Screen>
  );
}
